// PRC-048 (boceto S1, diseño 33 apartado 5): bloque «Actividad de compra» del
// Overview. Misma lectura que la página de Cadencia (get_pricing_cadence_attention_v2,
// no el Radar v1) y, para «En seguimiento», los episodios vinculados a un
// seguimiento abierto. Tabla densa a todo el ancho; arranca en Pendientes.
import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Link as RouterLink } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatEuro, formatInteger, formatPercent } from '../../../pricingJourney/formatNumber';
import {
  getMarginCenterCadenceArchivedFollowUpEpisodes,
  getMarginCenterCadenceAttention,
  getMarginCenterCadenceFollowUpEpisodes,
  getMarginCenterRelationshipActivity12m,
} from '../../services/marginCenterService';
import type {
  MarginCenterCadenceAttentionItem,
  MarginCenterCadenceAttentionLifecycle,
  MarginCenterCadenceAttentionResponse,
} from '../../types/cadenceAttention';
import CadenceManagementDrawer, { type CadenceManagementTarget } from './CadenceManagementDrawer';
import { cadenceDecisionAction } from './cadenceDecisionAction';
import { formatDayDate, margin12mLabel, sales12mValue, usualFrequencyLabel } from './cadenceManagementTexts';
import { cadenceRowKey, useDeferredCadenceActions, type CadenceListTab } from './useDeferredCadenceActions';

const PREVIEW = 8;
const PAGE = 50;

type Tab = CadenceListTab | 'all';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'pending', label: 'Pendientes' },
  { id: 'in_follow_up', label: 'En seguimiento' },
  { id: 'resolved', label: 'Archivadas' },
  { id: 'historical', label: 'Históricas sin resolver' },
  { id: 'all', label: 'Todas' },
];

/** Pestaña equivalente de la página de Cadencia (sin añadirle filtros). */
const CADENCE_LIFECYCLE: Record<Tab, MarginCenterCadenceAttentionLifecycle> = {
  pending: 'active_attention',
  historical: 'historical_unresolved',
  resolved: 'resolved',
  in_follow_up: 'active_attention',
  all: 'active_attention',
};

export interface PurchaseActivityRow {
  key: string;
  tab: CadenceListTab;
  customer_id: string;
  customer_label: string;
  product_id: string;
  product_label: string;
  first_outside_p90_date: string;
  last_purchase_date: string | null;
  days_since_last_purchase: number | null;
  p90_interval_days: number | null;
  resolution_id: string | null;
  resolution_type: string | null;
  conclusion: string | null;
  follow_up_id: string | null;
  next_action: string | null;
  next_review_at: string | null;
  /** Cambio PRC-049: referencia sustitutiva guardada al cerrar el seguimiento. */
  substitute_product_id?: string | null;
}

const num = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

const fromAttention = (item: MarginCenterCadenceAttentionItem, tab: CadenceListTab): PurchaseActivityRow => ({
  key: cadenceRowKey(item.customer_id, item.product_id, item.first_outside_p90_date),
  tab,
  customer_id: item.customer_id,
  customer_label: item.customer_label,
  product_id: item.product_id,
  product_label: item.product_label,
  first_outside_p90_date: item.first_outside_p90_date,
  last_purchase_date: item.facts?.last_purchase_date ?? item.history?.last_purchase_date ?? null,
  days_since_last_purchase: num(item.facts?.days_since_last_purchase ?? item.cadence?.days_since_last_purchase),
  p90_interval_days: num(item.facts?.p90_interval_days ?? item.cadence?.p90_interval_days),
  resolution_id: item.resolution?.id ?? null,
  resolution_type: item.resolution?.resolution_type ?? null,
  conclusion: item.resolution?.conclusion ?? null,
  follow_up_id: null,
  next_action: null,
  next_review_at: null,
});

const useAttentionPages = (lifecycle: MarginCenterCadenceAttentionLifecycle, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ['margin-center', 'cadence-attention', 'purchase-activity', lifecycle],
    queryFn: ({ pageParam }) => getMarginCenterCadenceAttention({ lifecycle, limit: PAGE, cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (last: MarginCenterCadenceAttentionResponse) => (last.page?.has_more ? last.page.next_cursor ?? undefined : undefined),
    enabled,
    staleTime: 30 * 1000,
  });

interface Props {
  /** Abre la relación con su episodio (lupa). */
  onOpenRow: (row: PurchaseActivityRow) => void;
  /** Enlace a la página de Cadencia en la pestaña indicada (con «Volver»). */
  cadenceHref: (lifecycle: MarginCenterCadenceAttentionLifecycle) => string;
}

const thSx = { textAlign: 'left', fontSize: 11, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#64748b', p: '6px 12px', borderBottom: '1px solid rgba(148,163,184,.16)', whiteSpace: 'nowrap' } as const;
const tdSx = { p: '6px 12px', height: 38, lineHeight: 1.25, borderBottom: '1px solid rgba(148,163,184,.08)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 340, fontSize: 13, verticalAlign: 'middle' } as const;
// En una tabla, max-width no se aplica a la celda: el recorte va en un bloque interior.
const clipSx = (max: number) => ({ maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) as const;
const numSx = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
const smallBtnSx = { height: 26, px: '10px', fontSize: 12, fontWeight: 700, borderRadius: '8px', fontFamily: J.font } as const;

const PurchaseActivityBlock: React.FC<Props> = ({ onOpenRow, cadenceHref }) => {
  const [tab, setTab] = useState<Tab>('pending');
  const [expanded, setExpanded] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState<{ target: CadenceManagementTarget; from: CadenceListTab } | null>(null);
  const deferred = useDeferredCadenceActions();

  const pendingQuery = useAttentionPages('active_attention', true);
  const historicalQuery = useAttentionPages('historical_unresolved', tab === 'historical' || tab === 'all');
  const resolvedQuery = useAttentionPages('resolved', tab === 'resolved' || tab === 'all');
  const followQuery = useInfiniteQuery({
    queryKey: ['margin-center', 'cadence-management', 'in-follow-up'],
    queryFn: ({ pageParam }) => getMarginCenterCadenceFollowUpEpisodes({ limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.page?.has_more ? pages.length * PAGE : undefined),
    staleTime: 30 * 1000,
  });
  // Cambio PRC-049: lo archivado al cerrar su seguimiento también es «Archivadas».
  const archivedFollowQuery = useInfiniteQuery({
    queryKey: ['margin-center', 'cadence-management', 'archived-follow-ups'],
    queryFn: ({ pageParam }) => getMarginCenterCadenceArchivedFollowUpEpisodes({ limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.page?.has_more ? pages.length * PAGE : undefined),
    staleTime: 30 * 1000,
  });

  const summary = pendingQuery.data?.pages?.[0]?.summary;
  const counts: Record<Tab, number | null> = (() => {
    const pending = deferred.adjustCount('pending', summary?.active_count);
    const historical = deferred.adjustCount('historical', summary?.historical_unresolved_count);
    // Si la lectura nueva falla, Archivadas sigue mostrando los archivos directos.
    const archivedFromFollowUp = archivedFollowQuery.isError
      ? 0 : archivedFollowQuery.data?.pages?.[0]?.summary?.archived_follow_up_count;
    const resolved = deferred.adjustCount('resolved', summary?.resolved_count == null || archivedFromFollowUp == null
      ? undefined : summary.resolved_count + archivedFromFollowUp);
    const inFollowUp = deferred.adjustCount('in_follow_up', followQuery.data?.pages?.[0]?.summary?.in_follow_up_count);
    const all = [pending, historical, resolved, inFollowUp].every((c) => c != null)
      ? (pending! + historical! + resolved! + inFollowUp!) : null;
    return { pending, historical, resolved, in_follow_up: inFollowUp, all };
  })();

  const rowsByTab = useMemo(() => {
    const flat = (q: typeof pendingQuery, t: CadenceListTab) =>
      (q.data?.pages ?? []).flatMap((p) => (p.items ?? []).map((i) => fromAttention(i, t)));
    const follow: PurchaseActivityRow[] = (followQuery.data?.pages ?? []).flatMap((p) => p.items.map((i) => ({
      key: cadenceRowKey(i.customer_id, i.product_id, i.first_outside_p90_date),
      tab: 'in_follow_up' as const,
      customer_id: i.customer_id,
      customer_label: i.customer_label,
      product_id: i.product_id,
      product_label: i.product_label,
      first_outside_p90_date: i.first_outside_p90_date,
      last_purchase_date: (i.facts?.last_purchase_date as string | null) ?? null,
      days_since_last_purchase: num(i.facts?.days_since_last_purchase),
      p90_interval_days: num(i.facts?.p90_interval_days),
      resolution_id: null,
      resolution_type: null,
      conclusion: null,
      follow_up_id: i.follow_up.id,
      next_action: i.follow_up.next_action,
      next_review_at: i.follow_up.next_review_at,
    })));
    const archivedFromFollowUp: PurchaseActivityRow[] = (archivedFollowQuery.data?.pages ?? []).flatMap((p) => p.items.map((i) => ({
      key: cadenceRowKey(i.customer_id, i.product_id, i.first_outside_p90_date),
      tab: 'resolved' as const,
      customer_id: i.customer_id,
      customer_label: i.customer_label,
      product_id: i.product_id,
      product_label: i.product_label,
      first_outside_p90_date: i.first_outside_p90_date,
      last_purchase_date: (i.facts?.last_purchase_date as string | null) ?? null,
      days_since_last_purchase: num(i.facts?.days_since_last_purchase),
      p90_interval_days: num(i.facts?.p90_interval_days),
      resolution_id: null,
      resolution_type: i.follow_up.resolution_type || (i.follow_up.close_reason === 'not_relevant' ? 'Sin relevancia comercial' : 'Situación explicada'),
      conclusion: i.follow_up.conclusion,
      follow_up_id: i.follow_up.id,
      next_action: null,
      next_review_at: null,
      substitute_product_id: i.follow_up.related_product_id,
    })));
    return {
      pending: flat(pendingQuery, 'pending'),
      historical: flat(historicalQuery, 'historical'),
      resolved: [...flat(resolvedQuery, 'resolved'), ...archivedFromFollowUp],
      in_follow_up: follow,
    };
  }, [pendingQuery.data, historicalQuery.data, resolvedQuery.data, followQuery.data, archivedFollowQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabRows: PurchaseActivityRow[] = (tab === 'all'
    ? [...rowsByTab.pending, ...rowsByTab.in_follow_up, ...rowsByTab.resolved, ...rowsByTab.historical]
    : rowsByTab[tab]
  ).filter((row) => !deferred.pendingKeys.has(row.key));
  const visibleRows = expanded ? tabRows : tabRows.slice(0, PREVIEW);

  const sources = tab === 'all'
    ? [pendingQuery, followQuery, resolvedQuery, archivedFollowQuery, historicalQuery]
    : tab === 'resolved'
      ? [resolvedQuery, archivedFollowQuery]
      : [{ pending: pendingQuery, historical: historicalQuery, in_follow_up: followQuery }[tab]];
  const nextSource = sources.find((q) => q.hasNextPage);
  const loading = sources.some((q) => q.isLoading);
  const failed = sources.some((q) => q.isError && q !== archivedFollowQuery);

  // Ventas, margen y días con compra de 12 meses (y la referencia sustitutiva de lo archivado).
  const activityKey = visibleRows.map((r) => `${r.key}|${r.resolution_id ?? ''}`).join(',');
  const activityQuery = useQuery({
    queryKey: ['margin-center', 'cadence-management', 'activity-rows', activityKey],
    queryFn: () => getMarginCenterRelationshipActivity12m(
      visibleRows.slice(0, 200).map((r) => ({ customer_id: r.customer_id, product_id: r.product_id, resolution_id: r.resolution_id })),
    ),
    enabled: visibleRows.length > 0,
    staleTime: 60 * 1000,
  });
  const activityByKey = useMemo(() => {
    const map = new Map<string, NonNullable<typeof activityQuery.data>['items'][number]>();
    (activityQuery.data?.items ?? []).forEach((a) => map.set(`${a.customer_id}|${a.product_id}|${a.resolution_id ?? ''}`, a));
    return map;
  }, [activityQuery.data]);
  const activityFor = (row: PurchaseActivityRow) => activityByKey.get(`${row.customer_id}|${row.product_id}|${row.resolution_id ?? ''}`);

  const openDrawer = (row: PurchaseActivityRow) => setDrawerTarget({
    from: row.tab,
    target: {
      customer_id: row.customer_id,
      customer_label: row.customer_label,
      product_id: row.product_id,
      product_label: row.product_label,
      first_outside_p90_date: row.first_outside_p90_date,
      last_purchase_date: row.last_purchase_date,
      days_since_last_purchase: row.days_since_last_purchase,
      p90_interval_days: row.p90_interval_days,
    },
  });

  // Columnas finales según la pestaña (anotación del boceto S1).
  const tail: 'sales' | 'follow' | 'resolved' = tab === 'in_follow_up' ? 'follow' : tab === 'resolved' ? 'resolved' : 'sales';
  const tailHeaders = tail === 'follow' ? ['Próxima acción', 'Revisión'] : tail === 'resolved' ? ['Motivo', 'Conclusión'] : ['Ventas 12 meses', 'Margen %'];
  const empty = pendingQuery.data?.pages?.[0]?.status === 'empty_dataset';

  return (
    <Box component="section" id="purchase-activity" sx={{ bgcolor: 'rgba(15,23,42,.66)', border: '1px solid rgba(148,163,184,.16)', borderRadius: '14px', p: '18px 20px', fontFamily: J.font, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText }}>Actividad de compra</Box>
          <Box component="h2" sx={{ fontSize: 18, fontWeight: 800, m: '4px 0', color: J.text }}>Relaciones con más tiempo sin compra de lo habitual</Box>
          <Box component="p" sx={{ fontSize: 13.5, color: J.muted, m: 0 }}>Relaciones cliente-producto cuyo tiempo desde la última compra supera su frecuencia habitual.</Box>
        </Box>
        <Box component={RouterLink} to={cadenceHref(CADENCE_LIFECYCLE[tab])} sx={{ color: J.skyText, fontSize: 13, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          Ver todo en Cadencia →
        </Box>
      </Box>

      <Box role="tablist" aria-label="Estado de los avisos" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: '10px', my: '14px' }}>
        {TABS.map((t) => {
          const on = tab === t.id;
          return (
            <ButtonBase
              key={t.id}
              role="tab"
              aria-selected={on}
              onClick={() => { setTab(t.id); setExpanded(false); }}
              sx={{
                border: '1px solid', borderColor: on ? J.sky : '#233149', borderRadius: '12px', p: '7px 12px',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', textAlign: 'left',
                bgcolor: on ? 'rgba(56,189,248,.08)' : 'transparent', fontFamily: J.font,
              }}
            >
              <Box component="span" sx={{ fontSize: 12.5, color: on ? J.skyText : J.muted }}>{t.label}</Box>
              <Box component="b" sx={{ fontSize: 18, fontWeight: 800, color: J.text, fontVariantNumeric: 'tabular-nums' }}>{formatInteger(counts[t.id])}</Box>
            </ButtonBase>
          );
        })}
      </Box>

      {empty ? (
        <Box sx={{ fontSize: 13, color: '#64748b' }}>No hay histórico transaccional suficiente para analizar la actividad de compra.</Box>
      ) : failed ? (
        <Box sx={{ fontSize: 13, color: J.dangerText }}>No se pudo cargar la actividad de compra. Inténtalo de nuevo en unos segundos.</Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: J.text }}>
            <thead>
              <tr>
                <Box component="th" sx={thSx}>Cliente · referencia</Box>
                <Box component="th" sx={{ ...thSx, ...numSx }}>Días sin compra</Box>
                <Box component="th" sx={{ ...thSx, ...numSx }}>Frecuencia habitual</Box>
                <Box component="th" sx={{ ...thSx, ...numSx }}>Última compra</Box>
                <Box component="th" sx={{ ...thSx, ...(tail === 'sales' ? numSx : {}) }}>{tailHeaders[0]}</Box>
                <Box component="th" sx={{ ...thSx, ...(tail === 'sales' ? numSx : {}) }}>{tailHeaders[1]}</Box>
                <Box component="th" sx={thSx} />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const a = activityFor(row);
                const canManage = row.tab === 'pending' || row.tab === 'historical';
                const motive = row.resolution_type
                  ? `${row.resolution_type}${(row.substitute_product_id ?? a?.substitute_product_id) ? ` · ${row.substitute_product_id ?? a?.substitute_product_id}` : ''}`
                  : '—';
                return (
                  <Box component="tr" key={`${row.tab}-${row.key}`} sx={{ '&:hover td': { bgcolor: 'rgba(56,189,248,.04)' } }}>
                    <Box component="td" sx={tdSx} title={`${row.customer_label} · ${row.product_id} · ${row.product_label}`}>
                      <Box component="span" sx={{ fontWeight: 800 }}>{row.customer_label}</Box>
                      <Box component="span" sx={{ ml: 1, fontSize: 12, color: J.muted }}>
                        {row.product_id}{row.product_label && row.product_label !== row.product_id ? ` · ${row.product_label}` : ''}
                      </Box>
                    </Box>
                    <Box component="td" sx={{ ...tdSx, ...numSx }}>{formatInteger(row.days_since_last_purchase)}</Box>
                    <Box component="td" sx={{ ...tdSx, ...numSx }}>{usualFrequencyLabel(row.p90_interval_days)}</Box>
                    <Box component="td" sx={{ ...tdSx, ...numSx }}>{formatDayDate(row.last_purchase_date)}</Box>
                    {tail === 'follow' ? (
                      <>
                        <Box component="td" sx={tdSx} title={row.next_action ?? ''}><Box sx={clipSx(320)}>{row.next_action || '—'}</Box></Box>
                        <Box component="td" sx={tdSx}>{formatDayDate(row.next_review_at)}</Box>
                      </>
                    ) : tail === 'resolved' ? (
                      <>
                        <Box component="td" sx={tdSx} title={motive}><Box sx={clipSx(260)}>{motive}</Box></Box>
                        <Box component="td" sx={tdSx} title={row.conclusion ?? ''}><Box sx={clipSx(340)}>{row.conclusion || '—'}</Box></Box>
                      </>
                    ) : (
                      <>
                        <Box component="td" sx={{ ...tdSx, ...numSx }}>{a ? formatEuro(sales12mValue(a), 0) : '…'}</Box>
                        <Box component="td" sx={{ ...tdSx, ...numSx }}>{a ? margin12mLabel(a, (v) => formatPercent(v)) : '…'}</Box>
                      </>
                    )}
                    <Box component="td" sx={{ ...tdSx, textAlign: 'right', width: 1 }}>
                      <Box sx={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        {canManage && (
                          <ButtonBase onClick={() => openDrawer(row)} sx={{ ...smallBtnSx, bgcolor: J.sky, color: J.onSky }}>Gestionar</ButtonBase>
                        )}
                        <Tooltip title="Ver la relación">
                          <IconButton aria-label="Ver la relación" onClick={() => onOpenRow(row)} sx={{ width: 26, height: 26, border: '1px solid #334155', borderRadius: '8px', color: J.skyText }}>
                            <SearchRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              {!loading && visibleRows.length === 0 && (
                <tr>
                  <Box component="td" colSpan={7} sx={{ ...tdSx, color: J.muted, whiteSpace: 'normal' }}>
                    No hay relaciones en esta pestaña.
                  </Box>
                </tr>
              )}
              {loading && visibleRows.length === 0 && (
                <tr><Box component="td" colSpan={7} sx={{ ...tdSx, color: J.muted }}>Cargando…</Box></tr>
              )}
            </tbody>
          </Box>
        </Box>
      )}

      {!empty && !failed && (tabRows.length > PREVIEW || (expanded && nextSource)) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '10px' }}>
          {!expanded ? (
            <ButtonBase onClick={() => setExpanded(true)} sx={{ color: J.skyText, fontSize: 13, fontWeight: 700, fontFamily: J.font }}>
              Ver todas ({formatInteger(counts[tab])})
            </ButtonBase>
          ) : nextSource ? (
            <ButtonBase disabled={nextSource.isFetchingNextPage} onClick={() => { void nextSource.fetchNextPage(); }} sx={{ color: J.skyText, fontSize: 13, fontWeight: 700, fontFamily: J.font }}>
              {nextSource.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </ButtonBase>
          ) : null}
        </Box>
      )}

      <CadenceManagementDrawer
        open={Boolean(drawerTarget)}
        target={drawerTarget?.target ?? null}
        onClose={() => setDrawerTarget(null)}
        onConfirm={(target, decision) => {
          deferred.schedule(cadenceDecisionAction(target, decision, drawerTarget?.from ?? 'pending'));
          setDrawerTarget(null);
        }}
      />
    </Box>
  );
};

export default PurchaseActivityBlock;
